import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography
} from '@mui/material';

import PriceTaxToggle from '../../Common/PriceTaxToggle';

export const emptyLineItem = () => ({
  productId: '',
  description: '',
  quantity: 1,
  unitPrice: '',
  taxPercentage: 0,
  priceIncludesTax: false,
  isOptional: false
});

export const emptyScheduleRow = () => ({
  label: '',
  amountType: 'percentage',
  value: 30,
  dueType: 'immediate',
  dueValue: 0
});

function scheduleSendCaption(s) {
  if (s.dueType === 'immediate') return 'Invoice will be sent immediately on acceptance.';
  if (s.dueType === 'days_from_accept') return `Invoice will be sent ${s.dueValue || 0} days after acceptance.`;
  if (s.dueType === 'days_before_event') return `Invoice will be sent ${s.dueValue || 0} days before the wedding.`;
  return '';
}

export function ProposalLineItemModal({ open, onClose, onSave, initial, products = [], title = 'Line item' }) {
  const [draft, setDraft] = useState(emptyLineItem());

  useEffect(() => {
    if (!open) return;
    if (!initial) {
      setDraft(emptyLineItem());
      return;
    }
    setDraft({
      ...emptyLineItem(),
      ...initial,
      productId: initial.productId || '',
      description: initial.description ?? '',
      quantity: initial.quantity ?? 1,
      unitPrice:
        initial.unitPrice === '' || initial.unitPrice === null || initial.unitPrice === undefined
          ? ''
          : String(initial.unitPrice),
      taxPercentage: parseFloat(initial.taxPercentage) || 0,
      priceIncludesTax: Boolean(initial.priceIncludesTax),
      isOptional: !!initial.isOptional
    });
  }, [open, initial]);

  const applyProduct = (productId) => {
    if (!productId) {
      setDraft((d) => ({ ...d, productId: '' }));
      return;
    }
    const product = products.find((p) => p._id === productId);
    if (!product) return;
    setDraft((d) => ({
      ...d,
      productId,
      description: product.name,
      unitPrice: product.price,
      taxPercentage: product.taxPercentage ?? 0,
      priceIncludesTax: Boolean(product.priceIncludesTax),
      isOptional: product.isOptional || false
    }));
  };

  const handleSave = () => {
    const desc = (draft.description || '').trim();
    const price = draft.unitPrice;
    if (!desc || price === '' || price === null || Number.isNaN(parseFloat(price))) {
      return;
    }
    onSave({
      ...draft,
      description: desc,
      quantity: Number(draft.quantity) || 1,
      unitPrice: parseFloat(draft.unitPrice) || 0,
      taxPercentage: parseFloat(draft.taxPercentage) || 0,
      priceIncludesTax: Boolean(draft.priceIncludesTax),
      isOptional: !!draft.isOptional
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Product (optional)</InputLabel>
              <Select
                value={draft.productId || ''}
                label="Product (optional)"
                onChange={(e) => applyProduct(e.target.value)}
              >
                <MenuItem value="">Custom</MenuItem>
                {products.map((p) => (
                  <MenuItem key={p._id} value={p._id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              required
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Quantity"
              value={draft.quantity}
              onChange={(e) => setDraft((d) => ({ ...d, quantity: e.target.value }))}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Unit price"
              required
              value={draft.unitPrice}
              onChange={(e) => setDraft((d) => ({ ...d, unitPrice: e.target.value }))}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="VAT (%)"
              value={draft.taxPercentage}
              onChange={(e) => setDraft((d) => ({ ...d, taxPercentage: e.target.value }))}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PriceTaxToggle
              value={Boolean(draft.priceIncludesTax)}
              onChange={(priceIncludesTax) => setDraft((d) => ({ ...d, priceIncludesTax }))}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!draft.isOptional}
                  onChange={(e) => setDraft((d) => ({ ...d, isOptional: e.target.checked }))}
                />
              }
              label="Optional add-on"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ProposalBillingScheduleModal({ open, onClose, onSave, initial, title = 'Billing schedule row' }) {
  const [draft, setDraft] = useState(emptyScheduleRow());

  useEffect(() => {
    if (!open) return;
    if (!initial) {
      setDraft(emptyScheduleRow());
      return;
    }
    setDraft({
      ...emptyScheduleRow(),
      ...initial,
      label: initial.label ?? '',
      amountType: initial.amountType || 'percentage',
      value: initial.value ?? 0,
      dueType: initial.dueType || 'immediate',
      dueValue: initial.dueValue ?? 0
    });
  }, [open, initial]);

  const caption = scheduleSendCaption(draft);

  const handleSave = () => {
    const label = (draft.label || '').trim();
    if (!label) return;
    onSave({
      ...draft,
      label,
      value: parseFloat(draft.value) || 0,
      dueValue: parseInt(draft.dueValue, 10) || 0
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Invoices are created when the agreement is accepted. Each row defines when that invoice will be sent.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Label"
              value={draft.label}
              onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
              placeholder="e.g. Deposit"
              required
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={draft.amountType}
                label="Type"
                onChange={(e) => setDraft((d) => ({ ...d, amountType: e.target.value }))}
              >
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="fixed">Fixed amount</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={draft.amountType === 'percentage' ? '%' : 'Amount'}
              value={draft.value}
              onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
              inputProps={{ min: 0, step: draft.amountType === 'percentage' ? 1 : 0.01 }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Send invoice</InputLabel>
              <Select
                value={draft.dueType}
                label="Send invoice"
                onChange={(e) => setDraft((d) => ({ ...d, dueType: e.target.value }))}
              >
                <MenuItem value="immediate">Immediately on accept</MenuItem>
                <MenuItem value="days_from_accept">Days after accept</MenuItem>
                <MenuItem value="days_before_event">Days before wedding</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Days"
              value={draft.dueValue}
              onChange={(e) => setDraft((d) => ({ ...d, dueValue: e.target.value }))}
              inputProps={{ min: 0 }}
              disabled={draft.dueType === 'immediate'}
            />
          </Grid>
          {caption && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                {caption}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!(draft.label || '').trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ProposalTermsModal({ open, onClose, value, onSave }) {
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (open) setDraft(value || '');
  }, [open, value]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Terms & conditions</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Shown on the published agreement after your layout blocks.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={8}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Enter your terms…"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => { onSave(draft); onClose(); }}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

